import type { CalculateChimneyInput } from "../domain/chimney/calculateChimney";
import { calculateChimneyHeightMm } from "../domain/chimney/calculateChimney";
import {
	type ChimneyStack,
	planChimneyStack,
} from "../domain/chimney/chimneyStack";
import { resolveWallHeightM } from "../domain/geometry/buildingDimensions";
import {
	DEFAULT_HOUSE_ABOVE_CEILING_M,
	normalizeIndoorRoom,
} from "../domain/indoor/indoorRoom";
import {
	createRectRoofGeometry,
	getRoofSlopeRunM,
} from "../domain/roof/rectRoofGeometry";
import type { FlueAxisM } from "./squareBaseBuilder/context";
import type { BuildInput } from "./types";

/**
 * The chimney a design buys, from its build input. `withChimney` quotes it and
 * the engines draw it, so the model shows exactly the modules on the list.
 */
/** The build-input fields the chimney reads. */
export type ChimneyBuildInput = Pick<
	BuildInput,
	| "dimensions"
	| "roofConfig"
	| "wallHeightM"
	| "overrides"
	| "siting"
	| "indoorRoom"
>;

/**
 * `flue` is where the chimney passes the roof, in the engines' sauna-centred
 * metres (`flueAxisFromLayout`, the drawn heater). With it the 1.0 m is
 * measured over the covering there; without it, over the ridge.
 */
export function chimneyInputFor(
	input: ChimneyBuildInput,
	flue?: FlueAxisM | null,
): CalculateChimneyInput {
	// The design's real interior ceiling height — the same rule the engines'
	// `ctx.H` and calculateHeaterRequirements read. This used to read
	// `dimensions.ceilingHeightMm`, which is the ELECTRICAL zone-height rule
	// ("Takhöjd (för el-zon)"): numerically 2100 today, but an admin override of
	// the actual ceiling height would never have reached the chimney while an
	// override of the el-zone height wrongly would.
	const ceilingMm =
		resolveWallHeightM(input.wallHeightM, input.overrides) * 1000;
	const pitchDeg = input.roofConfig?.pitchDeg ?? 12;
	const widthMm = input.dimensions.width * 1000;
	const lengthMm = input.dimensions.length * 1000;
	// Horizontal run the pitch is applied over, eave → ridge/high edge. This has
	// to match `createRectRoofGeometry`: a gable's rise is `(width/2)·tan θ`, but
	// a MONO roof's fall runs over its slope run — the length for a front/back
	// high wall (`length · tan θ`), the width for a left/right one. Passing the
	// width for a front-high mono understated a 2.5×4.0 sauna's rise by 211 mm.
	const roofSpanMm =
		input.roofConfig?.kind === "mono"
			? getRoofSlopeRunM(widthMm, lengthMm, input.roofConfig)
			: widthMm / 2;
	// Indoors (issue 175) the flue runs through the house to ITS ridge; the
	// sauna's flat ceiling has no roof of its own to clear.
	const indoorRoom =
		input.siting === "indoor" ? normalizeIndoorRoom(input.indoorRoom) : null;
	return indoorRoom
		? {
				ceilingMm,
				roofAngleDeg: 0,
				houseAboveCeilingMm:
					(indoorRoom.houseAboveCeilingM ?? DEFAULT_HOUSE_ABOVE_CEILING_M) *
					1000,
				houseAboveAssumed: indoorRoom.houseAboveCeilingM === null,
			}
		: {
				ceilingMm,
				roofAngleDeg: pitchDeg,
				roofSpanMm,
				...flueRoofRise(input, flue),
			};
}

/** Roof rise at the flue over the eave, for a pitched rectangular roof. */
function flueRoofRise(
	input: ChimneyBuildInput,
	flue: FlueAxisM | null | undefined,
): { flueRoofRiseMm?: number } {
	const roofConfig = input.roofConfig;
	if (!flue || (roofConfig?.kind !== "gable" && roofConfig?.kind !== "mono")) {
		return {};
	}
	const roof = createRectRoofGeometry({
		width: input.dimensions.width,
		length: input.dimensions.length,
		wallHeight: 0,
		roofConfig,
	});
	const riseM = Math.min(
		roof.roofRise,
		Math.max(0, roof.getRoofHeightAt(flue.x, flue.z)),
	);
	return { flueRoofRiseMm: riseM * 1000 };
}

/** The purchased module stack, as the engines draw it. */
export interface DrawnChimney extends ChimneyStack {
	/**
	 * Indoors the run continues through a house the model does not draw, so the
	 * engines stop one section above the sauna roof instead of standing the
	 * whole stack in mid-air.
	 */
	throughHouse: boolean;
}

/** The purchased module stack for a wood-fired design. */
export function chimneyStackFor(
	input: ChimneyBuildInput,
	flue?: FlueAxisM | null,
): DrawnChimney {
	const chimneyInput = chimneyInputFor(input, flue);
	return {
		...planChimneyStack(
			calculateChimneyHeightMm(chimneyInput),
			chimneyInput.ceilingMm,
			chimneyInput.flueOutletHeightMm,
		),
		throughHouse: chimneyInput.houseAboveCeilingMm !== undefined,
	};
}
